import { Type } from '@angular/core';
import { Label } from '../../labels/englishLabels';
import { InventoryResourceComponent } from '../../views/inventory-resource/inventory-resource.component';
import { ViolationComponent } from '../../views/violation/violation.component';
import { SidePanelViewModel } from './types/side-panel';

const violationKeyboardShortcuts = Label.get('keyboardShortcuts.violations');
const resourcesKeyboardShortcuts = Label.get('keyboardShortcuts.resources');

/**
 * Panel registry entry defining how to create a panel for a given type.
 * Only used for stackable panel types (resource, violation).
 */
export interface PanelRegistryEntry {
  component: Type<unknown>;
  inputs: (id: string, integration: string, viewModel?: SidePanelViewModel) => Record<string, unknown>;
  shortcuts: string;
}

/**
 * Registry for STACKABLE panel types only.
 * These panels support the stacked URL format: ?integration=abc&s0=resource:id1&s1=violation:id2
 *
 * Non-stackable panels (issue, finding, agentStep, findingThread, etc.) are handled separately
 * by the side panel service without going through this registry.
 */
export const PANEL_REGISTRY: Record<string, PanelRegistryEntry> = {
  resource: {
    component: InventoryResourceComponent,
    inputs: (id: string, integration: string, viewModel?: SidePanelViewModel) => ({
      resource: id,
      integration,
      viewModel,
    }),
    shortcuts: resourcesKeyboardShortcuts,
  },
  violation: {
    component: ViolationComponent,
    inputs: (id: string, integration: string, viewModel?: SidePanelViewModel) => ({
      violation: id,
      integration,
      viewModel,
    }),
    shortcuts: violationKeyboardShortcuts,
  },
};

/**
 * Supported stackable panel types
 */
export type StackablePanelType = keyof typeof PANEL_REGISTRY;

/**
 * Check if a panel type is supported for stacking
 */
export function isStackablePanelType(type: string): type is StackablePanelType {
  return type in PANEL_REGISTRY;
}

/**
 * Parse a stack entry string (e.g., "resource:abc123" or "violation:xyz:extra")
 * Format: type:id or type:id:additionalParams
 */
export function parseStackEntry(entry: string): { type: string; id: string } | null {
  const colonIndex = entry.indexOf(':');
  if (colonIndex === -1) {
    return null;
  }
  const type = entry.substring(0, colonIndex);
  const id = entry.substring(colonIndex + 1);
  if (!type || !id) {
    return null;
  }
  return { type, id };
}

/**
 * Create a stack entry string from type and id
 */
export function createStackEntry(type: string, id: string): string {
  return `${type}:${id}`;
}

/**
 * Stack param prefix for URL params (s0, s1, s2, etc.)
 */
export const STACK_PARAM_PREFIX = 's';

/**
 * Check if a param key is a stack param (s0, s1, s2, etc.)
 */
export function isStackParam(key: string): boolean {
  return /^s\d+$/.test(key);
}

/**
 * Get the index from a stack param (s0 -> 0, s1 -> 1, etc.)
 */
export function getStackParamIndex(key: string): number {
  const match = key.match(/^s(\d+)$/);
  return match ? parseInt(match[1], 10) : -1;
}

/**
 * Create a stack param key from an index (0 -> s0, 1 -> s1, etc.)
 */
export function createStackParamKey(index: number): string {
  return `${STACK_PARAM_PREFIX}${index}`;
}
